/**
 * Shared date-range presets for the attendance monitor and the dashboard
 * (FEAT-2026-006b).
 *
 * Extracted from `Attendance.tsx` so both pages offer the same presets and
 * resolve them identically — two copies would drift and quietly disagree
 * about what "Last 7 days" means.
 *
 * Every date here is a **calendar date in the tenant timezone** (YYYY-MM-DD),
 * matching what the attendance/report endpoints filter on.
 */

import { browserTimeZone } from "./datetime";

export type RangePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "custom";

export interface DateRange {
  from: string;
  to: string;
}

/**
 * Today's calendar date in `tz` (the tenant timezone), as YYYY-MM-DD.
 *
 * Mirrors the backend's `dateInTz` so both ends agree on which day "today" is.
 * The old implementation was `new Date().toISOString().slice(0,10)` — the **UTC**
 * date, which in Malaysia (UTC+8) reports *yesterday* from 00:00 to 08:00 local,
 * so the dashboard labelled yesterday's numbers "Today" every morning.
 *
 * Defaults to the browser's timezone, which is the best guess before the tenant
 * settings have loaded (and already correct for staff on local machines).
 */
export function todayIso(tz: string = browserTimeZone()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Calendar arithmetic on a YYYY-MM-DD string. Deliberately timezone-free: the
 * parts are read and written in UTC, so the result depends only on the input
 * string. (Parsing as *local* midnight and serialising with `toISOString()` —
 * the previous implementation — silently subtracted a day in every UTC+ zone:
 * in Asia/Kuala_Lumpur `addDaysIso("2026-07-28", 0)` returned "2026-07-27".)
 */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** First day of the month `iso` falls in. Pure string math — no Date involved. */
export function startOfMonthIso(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function rangeForPreset(
  preset: Exclude<RangePreset, "custom">,
  anchor: string = todayIso(),
): DateRange {
  switch (preset) {
    case "today":
      return { from: anchor, to: anchor };
    case "yesterday": {
      const y = addDaysIso(anchor, -1);
      return { from: y, to: y };
    }
    case "last7":
      return { from: addDaysIso(anchor, -6), to: anchor };
    case "last30":
      return { from: addDaysIso(anchor, -29), to: anchor };
    case "thisMonth":
      return { from: startOfMonthIso(anchor), to: anchor };
  }
}

/**
 * Which preset (if any) a range corresponds to — keeps the chip UI honest.
 * `today` must be resolved in the same timezone the range was built from,
 * otherwise a range built in tenant time won't match any preset here.
 */
export function presetForRange(r: DateRange, today: string = todayIso()): RangePreset {
  for (const p of ["today", "yesterday", "last7", "last30", "thisMonth"] as const) {
    const exp = rangeForPreset(p, today);
    if (exp.from === r.from && exp.to === r.to) return p;
  }
  return "custom";
}

export function formatRangeLabel(r: DateRange): string {
  return r.from === r.to ? r.from : `${r.from} → ${r.to}`;
}

export const PRESET_LABELS: { value: Exclude<RangePreset, "custom">; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
];

/**
 * Date params for the attendance/report endpoints. A single day is sent as
 * `date` (the legacy shape most callers still use); a span becomes
 * `dateFrom`/`dateTo`.
 */
export function rangeToQuery(r: DateRange): {
  date?: string;
  dateFrom?: string;
  dateTo?: string;
} {
  return r.from === r.to ? { date: r.from } : { dateFrom: r.from, dateTo: r.to };
}
